import { useCallback, useEffect, useState } from 'react';

/* =============================================================================
   usePortalTheme — opt-in, portal-scoped theme selection (ADDITIVE)
   -----------------------------------------------------------------------------
   Owns the portal's theme selection: persists it to localStorage and resolves
   it to the data-theme attribute value applied to the portal wrapper
   (.portal-shell). 'default' resolves to null → the attribute is omitted
   entirely, so the portal renders byte-identical to today. The public site
   never mounts the portal wrapper, so it is unaffected regardless.

   Adding another [data-theme] option later (e.g. a warm-charcoal "dark") is a
   data change: add it to VALID + THEME_OPTIONS (ThemePicker) and add a matching
   token block in styles/tokens/colors.css. No rewrite.
   ============================================================================= */

export const PORTAL_THEME_STORAGE_KEY = 'cyv-portal-theme';
export const DEFAULT_PORTAL_THEME = 'default';

// Stored selections the picker understands. 'system' and 'comfort-system'
// resolve at runtime via prefers-color-scheme; the others map 1:1 (or to null
// for 'default'). Light / Dark / Match device are the main three since
// 2026-10-30 (Anthony: "navy should be the default Dark Mode, Comfort options
// should be supplemental"), so 'system' pairs Default with Navy.
const VALID = ['default', 'navy', 'system', 'comfort', 'comfort-dark', 'comfort-system'];

function readStored() {
	try {
		const v = window.localStorage.getItem(PORTAL_THEME_STORAGE_KEY);
		return VALID.includes(v) ? v : DEFAULT_PORTAL_THEME;
	} catch {
		return DEFAULT_PORTAL_THEME;
	}
}

function systemPrefersDark() {
	try {
		return window.matchMedia('(prefers-color-scheme: dark)').matches;
	} catch {
		return false;
	}
}

// selection + system preference → the data-theme attribute value (or null).
function resolveAttr(selection, prefersDark) {
	switch (selection) {
		case 'comfort':
			return 'comfort';
		case 'comfort-dark':
			return 'comfort-dark';
		case 'comfort-system':
			return prefersDark ? 'comfort-dark' : 'comfort';
		case 'navy':
			return 'navy';
		case 'system':
			return prefersDark ? 'navy' : null;
		default:
			return null; // 'default' — no attribute, untouched look
	}
}

export function usePortalTheme() {
	const [selection, setSelectionState] = useState(readStored);
	const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

	// System scheme only affects the two match-device selections, but tracking it
	// always keeps the resolved value correct the moment the user picks it.
	useEffect(() => {
		let mq;
		try {
			mq = window.matchMedia('(prefers-color-scheme: dark)');
		} catch {
			return undefined;
		}
		const onChange = (e) => setPrefersDark(e.matches);
		mq.addEventListener('change', onChange);
		return () => mq.removeEventListener('change', onChange);
	}, []);

	const setSelection = useCallback((next) => {
		const value = VALID.includes(next) ? next : DEFAULT_PORTAL_THEME;
		setSelectionState(value);
		try {
			if (value === DEFAULT_PORTAL_THEME) {
				window.localStorage.removeItem(PORTAL_THEME_STORAGE_KEY);
			} else {
				window.localStorage.setItem(PORTAL_THEME_STORAGE_KEY, value);
			}
		} catch {
			/* localStorage unavailable — selection still applies this session */
		}
	}, []);

	const appliedTheme = resolveAttr(selection, prefersDark);
	return { selection, setSelection, appliedTheme };
}
